import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostKalkulationHochladenRequest, IPostKalkulationHochladenResponse } from 'fa-kt.types';
import { PostKalkulationHochladenCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-kalkulation-hochladen',
  templateUrl: './post-kalkulation-hochladen.component.html',
  styleUrls: ['./post-kalkulation-hochladen.component.css'],
})
export class PostKalkulationHochladenCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostKalkulationHochladenRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostKalkulationHochladenRequest> = new EventEmitter<IPostKalkulationHochladenRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostKalkulationHochladenResponse> = new EventEmitter<IPostKalkulationHochladenResponse>();

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postKalkulationHochladen: PostKalkulationHochladenCommandService /* << more    */ /*    more >> */,
  ) {
    this.command = sitemap.GUTACHTEN.Commands.POST_KALKULATION_HOCHLADEN;
    this.form = this.fb.group({
      kalkulation: this.fb.group({
        id: [null, []],
        gutachten: [null, [Validators.required]],
        datei: [null, [Validators.required]],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postKalkulationHochladen.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostKalkulationHochladenRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.kalkulationId?.valueChanges.subscribe(async (id) => {
        /* << on-kalkulation-id-change    */
        /*    on-kalkulation-id-change >> */
      }) as Subscription,
      this.kalkulationGutachten?.valueChanges.subscribe(async (gutachten) => {
        /* << on-kalkulation-gutachten-change    */
        /*    on-kalkulation-gutachten-change >> */
      }) as Subscription,
      this.kalkulationDatei?.valueChanges.subscribe(async (datei) => {
        /* << on-kalkulation-datei-change    */
        /*    on-kalkulation-datei-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostKalkulationHochladenRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.kalkulation) {
        value.kalkulation = cleanObject(value.kalkulation);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.kalkulation) {
        payload.kalkulation = cleanObject(payload.kalkulation);
      }
      const response: IPostKalkulationHochladenResponse = await this.postKalkulationHochladen.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get kalkulationId() {
    return this.form.controls.kalkulation.get('id');
  }
  get kalkulationGutachten() {
    return this.form.controls.kalkulation.get('gutachten');
  }
  get kalkulationDatei() {
    return this.form.controls.kalkulation.get('datei');
  }
}
